"use client";
import {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useMemo,
} from "react";
import { StartAsPage } from "@/app/[locale]/auth/login/components/types";
import AuthModal from "@/components/AuthModal";
import { useAuth } from "@/components/AuthProvider";

// Modal state context - changes frequently (triggers re-renders)
interface ModalStateContextType {
  isOpen: boolean;
  startAs: StartAsPage;
  modalCallback: (() => void) | undefined;
}

// Modal actions context - stable, rarely changes (doesn't trigger re-renders)
interface ModalActionsContextType {
  openLoginModal: (callback?: () => void) => void;
  openRegisterModal: (callback?: () => void) => void;
  openForgotPasswordModal: () => void;
  closeModal: () => void;
  updateLoggedInStatus: () => void;
}

const ModalStateContext = createContext<ModalStateContextType | undefined>(
  undefined,
);

const ModalActionsContext = createContext<ModalActionsContextType | undefined>(
  undefined,
);

export const useModalState = (): ModalStateContextType => {
  const context = useContext(ModalStateContext);
  if (context === undefined) {
    throw new Error("useModalState must be used within an AuthModalProvider");
  }
  return context;
};

export const useModalActions = (): ModalActionsContextType => {
  const context = useContext(ModalActionsContext);
  if (context === undefined) {
    throw new Error("useModalActions must be used within an AuthModalProvider");
  }
  return context;
};

interface AuthModalProviderProps {
  children: ReactNode;
}

export function AuthModalProvider({ children }: AuthModalProviderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [startAs, setStartAs] = useState<StartAsPage>(StartAsPage.Login);
  const [modalCallback, setModalCallback] = useState<(() => void) | undefined>(
    undefined,
  );

  const { refreshStatus } = useAuth();

  const updateLoggedInStatus = useCallback(() => {
    refreshStatus();
  }, [refreshStatus]);

  const openLoginModal = useCallback((callback?: () => void) => {
    if (callback) {
      setModalCallback(() => callback);
    }
    setStartAs(StartAsPage.Login);
    setIsOpen(true);
  }, []);

  const openRegisterModal = useCallback((callback?: () => void) => {
    if (callback) {
      setModalCallback(() => callback);
    }
    setStartAs(StartAsPage.Register);
    setIsOpen(true);
  }, []);

  const openForgotPasswordModal = useCallback(() => {
    setStartAs(StartAsPage.ForgotPassword);
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setModalCallback(undefined);
  }, []);

  // Memoize the state context value to prevent unnecessary re-renders
  const stateValue = useMemo<ModalStateContextType>(
    () => ({
      isOpen,
      startAs,
      modalCallback,
    }),
    [isOpen, startAs, modalCallback],
  );

  // Memoize the actions context value to prevent unnecessary re-renders
  const actionsValue = useMemo<ModalActionsContextType>(
    () => ({
      openLoginModal,
      openRegisterModal,
      openForgotPasswordModal,
      closeModal,
      updateLoggedInStatus,
    }),
    [
      openLoginModal,
      openRegisterModal,
      openForgotPasswordModal,
      closeModal,
      updateLoggedInStatus,
    ],
  );

  return (
    <ModalStateContext.Provider value={stateValue}>
      <ModalActionsContext.Provider value={actionsValue}>
        {children}
        <AuthModal
          key={isOpen ? startAs : "closed"}
          isOpen={isOpen}
          onClose={closeModal}
          startAs={startAs}
          updateLoggedInStatus={updateLoggedInStatus}
        />
      </ModalActionsContext.Provider>
    </ModalStateContext.Provider>
  );
}
